const MAX_IMAGE_UPLOAD_BYTES = 500 * 1024;
const WEBP_MIME_TYPE = "image/webp";

const isImageFile = (file) =>
  file instanceof Blob && file?.type?.startsWith("image/");

const getImageFileName = (name = "upload", mimeType) => {
  const baseName = name.replace(/\.[^.]+$/, "") || "upload";
  return `${baseName}.${mimeType === "image/png" ? "png" : "webp"}`;
};

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Unable to process image upload."));
    };

    image.src = url;
  });

const canvasToBlob = (canvas, quality, mimeType) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob && blob.type === mimeType) resolve(blob);
        else reject(new Error(`Unable to convert image to ${mimeType}.`));
      },
      mimeType,
      quality,
    );
  });

const drawImageToCanvas = (image, width, height) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(image, 0, 0, width, height);

  return canvas;
};

export const processImageForUpload = async (
  file,
  { maxBytes = MAX_IMAGE_UPLOAD_BYTES, maxSide = 4000, minSide = 64, mimeType = WEBP_MIME_TYPE } = {},
) => {
  if (!isImageFile(file)) return file;

  const image = await loadImage(file);
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;

  if (!sourceWidth || !sourceHeight) {
    throw new Error("Unable to read image dimensions.");
  }

  const sourceMaxSide = Math.max(sourceWidth, sourceHeight);
  let scale = sourceMaxSide > maxSide ? maxSide / sourceMaxSide : 1;
  let width = Math.max(1, Math.round(sourceWidth * scale));
  let height = Math.max(1, Math.round(sourceHeight * scale));
  const fileName = getImageFileName(file.name, mimeType);

  while (Math.max(width, height) >= minSide) {
    const canvas = drawImageToCanvas(image, width, height);

    // PNG is lossless and ignores the canvas quality parameter.
    const qualities = mimeType === "image/png" ? [undefined] : [0.92, 0.82, 0.72, 0.62, 0.52, 0.42, 0.35, 0.28];
    for (const quality of qualities) {
      const blob = await canvasToBlob(canvas, quality, mimeType);

      if (blob.size <= maxBytes) {
        return new File([blob], fileName, {
          type: mimeType,
          lastModified: Date.now(),
        });
      }
    }

    width = Math.max(1, Math.round(width * 0.82));
    height = Math.max(1, Math.round(height * 0.82));
  }

  throw new Error("Image must be compressible below 500KB before upload.");
};

export const processFilesForUpload = async (files = [], options) => {
  const fileList = Array.from(files || []);
  return Promise.all(fileList.map((file) => processImageForUpload(file, options)));
};

export const appendProcessedFile = async (formData, fieldName, file) => {
  formData.append(fieldName, await processImageForUpload(file));
};

export const appendProcessedFiles = async (formData, fieldName, files = [], options) => {
  const processedFiles = await processFilesForUpload(files, options);
  processedFiles.forEach((file) => formData.append(fieldName, file));
  return processedFiles;
};
